/**
 * Legends Component
 *
 * Displays a set of color-coded legend items, typically used to explain
 * the meaning of colors in a booking schedule or calendar view.
 *
 * @example
 * ```tsx
 * <Legends />
 *
 * // Or with custom legends:
 * <Legends
 *   legends={[
 *     { name: 'Available', color: 'green' },
 *     { name: 'Busy', color: 'red' },
 *   ]}
 * />
 * ```
 */

import classNames from "classnames";
import React from "react";
import styles from "./Legends.module.css";
import type { LegendItem, LegendsProps } from "./types";
import { DEFAULT_BOOKING_LEGENDS } from "./types";

export const Legends = React.forwardRef<HTMLUListElement, LegendsProps>(
  (
    {
      legends = DEFAULT_BOOKING_LEGENDS,
      direction = "row",
      align = "start",
      className,
      "data-testid": testId,
    },
    ref,
  ) => {
    // Container size class

    const getLegendColorStyle = (legend: LegendItem): React.CSSProperties => ({
      backgroundColor: legend.color,
    });

    return (
      <ul
        ref={ref}
        role="list"
        aria-label="Booking status legend"
        className={classNames(styles.legendsContainer, className)}
        data-testid={testId}
        data-direction={direction}
        data-align={align}
      >
        {legends.map((legend, index) => (
          <li
            key={legend.id ?? `legend-${index}`}
            role="listitem"
            className={classNames(
              styles.item,
              direction === "column" && styles.itemColumn,
            )}
            data-testid={
              testId ? `${testId}-item-${legend.id ?? index}` : undefined
            }
          >
            <span
              className={classNames(styles.colorSwatch)}
              style={getLegendColorStyle(legend)}
              data-component-name="color-swatch"
              aria-hidden="true"
            />
            <span data-component-name="label">{legend.name}</span>
          </li>
        ))}
      </ul>
    );
  },
);

Legends.displayName = "Legends";
